/**
 * Formulário do protocolo de assinatura do CASO (E74.F1; E78.F2, ADR-0077).
 *
 * Controlado com `useState`, sem biblioteca de formulário: o repositório não
 * usa nenhuma e o contrato é plano o bastante. Duas ações:
 *   - **Salvar protocolo** (PUT) — grava a transcrição; substituir invalida a
 *     verificação anterior.
 *   - **Verificar** (POST) — roda o motor sobre o protocolo SALVO. Fica travado
 *     enquanto não há protocolo salvo, enquanto o formulário tem alteração não
 *     salva (verificaria o de antes) e enquanto há PUT/POST em voo.
 *   - **Atualizar fontes externas** (POST com `atualizar_consultas`) — mesma
 *     verificação, mas refazendo as consultas às fontes cadastrais, de
 *     localização e de rede. É a única ação que gasta consulta paga, por isso
 *     é um botão separado e discreto, não o caminho padrão.
 *
 * Durante a mutação o formulário inteiro trava (memória: trave o contexto
 * durante a mutação). Quem faz as chamadas é a página: aqui só o estado da
 * transcrição e a validação do que o servidor recusaria.
 */
import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ProtocoloAssinaturaData } from '@/lib/exame_eletronico';

import {
  estadoDoProtocolo,
  protocoloDoEstado,
  validarProtocoloForm,
  type ProtocoloFormErros,
  type ProtocoloFormState,
} from './protocolo_form_state';
import { SecaoEventos, SecaoIps } from './ProtocoloFormListas';
import {
  SecaoBiometria,
  SecaoCoordenada,
  SecaoDispositivo,
  SecaoIdentificacao,
  SecaoObservacoes,
  SecaoSignatario,
} from './ProtocoloFormSecoes';

const t = ptBR.exameEletronico.form;

export type ProtocoloFormAcao = 'salvando' | 'verificando' | 'atualizando';

export interface ProtocoloFormProps {
  /** Prefixo de `id`/`data-testid` (uma CCB por formulário na mesma página). */
  idPrefix: string;
  /** Protocolo já salvo, ou `null` quando ainda não foi informado. */
  protocoloInicial: ProtocoloAssinaturaData | null;
  /** Ação em voo — trava o formulário e os dois botões. */
  acaoEmCurso: ProtocoloFormAcao | null;
  /** O perito concluiu alguma peça: nada mais pode ser alterado (ADR-0077 §2.4). */
  concluido: boolean;
  /**
   * E78.F2 — esconde o código declarado, que virou dado da PEÇA. Ligado no
   * protocolo do caso; desligado onde o formulário ainda edita uma peça só.
   */
  ocultarCodigoDeclarado?: boolean;
  /** Rótulo do botão de verificar — "as N peças" no escopo do caso. */
  rotuloVerificar?: string;
  /**
   * E78.F3 — desliga as duas verificações aqui dentro: no card colapsado elas
   * ficam visíveis mesmo com o formulário fechado, então quem as apresenta é o
   * card. Ligado por padrão, que é como a tela usava o formulário até aqui.
   */
  mostrarAcoesDeVerificacao?: boolean;
  /**
   * E78.F3 — avisa quem está por fora que há alteração não salva. É o que trava
   * o "Verificar" quando o botão mora no card: verificar agora rodaria o motor
   * sobre o protocolo ANTERIOR, sem que o usuário percebesse.
   */
  onAlterado?: () => void;
  onSalvar: (protocolo: ProtocoloAssinaturaData) => void;
  onVerificar: () => void;
  onAtualizarConsultas: () => void;
}

/** Rótulo do botão: gira quando a ação correspondente está em voo. */
function Rotulo({ girando, texto }: { girando: boolean; texto: string }): JSX.Element {
  if (!girando) return <>{texto}</>;
  return (
    <>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {texto}
    </>
  );
}

interface AcoesProps {
  idPrefix: string;
  acaoEmCurso: ProtocoloFormAcao | null;
  disabled: boolean;
  podeVerificar: boolean;
  /** Há alteração não salva (ou nada salvo): as duas verificações ficam travadas. */
  precisaSalvar: boolean;
  rotuloVerificar?: string;
  onVerificar: () => void;
  onAtualizarConsultas: () => void;
}

function Acoes({
  idPrefix,
  acaoEmCurso,
  disabled,
  podeVerificar,
  precisaSalvar,
  rotuloVerificar,
  onVerificar,
  onAtualizarConsultas,
}: AcoesProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-sc-3 border-t border-border pt-sc-4">
      <Button type="submit" disabled={disabled} data-testid={`${idPrefix}-salvar`}>
        <Rotulo
          girando={acaoEmCurso === 'salvando'}
          texto={acaoEmCurso === 'salvando' ? t.salvando : t.salvar}
        />
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={!podeVerificar}
        onClick={onVerificar}
        data-testid={`${idPrefix}-verificar`}
        {...(precisaSalvar ? { title: t.verificarHint } : {})}
      >
        <Rotulo
          girando={acaoEmCurso === 'verificando'}
          texto={acaoEmCurso === 'verificando' ? t.verificando : (rotuloVerificar ?? t.verificar)}
        />
      </Button>
      <Button
        type="button"
        variant="ghost"
        disabled={!podeVerificar}
        onClick={onAtualizarConsultas}
        data-testid={`${idPrefix}-atualizar-consultas`}
        title={t.atualizarConsultasHint}
      >
        <Rotulo
          girando={acaoEmCurso === 'atualizando'}
          texto={acaoEmCurso === 'atualizando' ? t.atualizandoConsultas : t.atualizarConsultas}
        />
      </Button>
      {precisaSalvar ? (
        <p className="text-caption text-fg-dim" data-testid={`${idPrefix}-verificar-hint`}>
          {t.verificarHint}
        </p>
      ) : null}
    </div>
  );
}

export function ProtocoloForm({
  idPrefix,
  protocoloInicial,
  acaoEmCurso,
  concluido,
  ocultarCodigoDeclarado = false,
  rotuloVerificar,
  mostrarAcoesDeVerificacao = true,
  onAlterado,
  onSalvar,
  onVerificar,
  onAtualizarConsultas,
}: ProtocoloFormProps): JSX.Element {
  const [state, setState] = useState<ProtocoloFormState>(() => estadoDoProtocolo(protocoloInicial));
  const [erros, setErros] = useState<ProtocoloFormErros>({});
  const [alterado, setAlterado] = useState(false);

  const emVoo = acaoEmCurso !== null;
  const disabled = emVoo || concluido;
  const temProtocoloSalvo = protocoloInicial !== null;
  const podeVerificar = temProtocoloSalvo && !alterado && !disabled;

  function patch(parcial: Partial<ProtocoloFormState>): void {
    setState((atual) => ({ ...atual, ...parcial }));
    setAlterado(true);
    onAlterado?.();
  }

  function handleSubmit(evt: FormEvent<HTMLFormElement>): void {
    evt.preventDefault();
    const encontrados = validarProtocoloForm(state);
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) return;
    onSalvar(protocoloDoEstado(state));
  }

  const secao = { idPrefix, state, patch, disabled, erros };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-sc-6"
      data-testid={`${idPrefix}-form`}
      aria-busy={emVoo}
    >
      <div>
        <h3 className="text-body-md font-semibold text-fg">{t.titulo}</h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      <SecaoIdentificacao {...secao} ocultarCodigoDeclarado={ocultarCodigoDeclarado} />
      <SecaoSignatario {...secao} />
      <SecaoEventos {...secao} />
      <SecaoIps {...secao} />
      <SecaoCoordenada {...secao} />
      <SecaoDispositivo {...secao} />
      <SecaoBiometria {...secao} />
      <SecaoObservacoes {...secao} />

      {mostrarAcoesDeVerificacao ? (
        <Acoes
          idPrefix={idPrefix}
          acaoEmCurso={acaoEmCurso}
          disabled={disabled}
          podeVerificar={podeVerificar}
          precisaSalvar={!temProtocoloSalvo || alterado}
          {...(rotuloVerificar !== undefined ? { rotuloVerificar } : {})}
          onVerificar={onVerificar}
          onAtualizarConsultas={onAtualizarConsultas}
        />
      ) : (
        <div className="border-t border-border pt-sc-4">
          <Button type="submit" disabled={disabled} data-testid={`${idPrefix}-salvar`}>
            <Rotulo
              girando={acaoEmCurso === 'salvando'}
              texto={acaoEmCurso === 'salvando' ? t.salvando : t.salvar}
            />
          </Button>
        </div>
      )}
    </form>
  );
}
